import moment from "moment-timezone";
import React, { useContext } from "react";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import ThemeContext from "../../utils/Contexts/ThemeContext";
import AmericanTransaction from "../svg/AmericanTransaction";
import AppleTransaction from "../svg/AppleTransaction";
import DinersTransaction from "../svg/DinersTransaction";
import DiscoverTransaction from "../svg/DiscoverTransaction";
import EftposTransaction from "../svg/EftposAustraliaTransaction";
import GirocardTransaction from "../svg/GirocardTransaction";
import GoogleTransaction from "../svg/GoogleTransaction";
import JcbTransaction from "../svg/JcbTransaction";
import MatercardTransaction from "../svg/MastercardTransaction";
import PaypalTransaction from "../svg/paypalTransaction";
import UnionTransaction from "../svg/UnionpayTransaction";
import VisaTransaction from "../svg/VisaTransaction";

function TransactionsTable({
  data,
  columnsVisibility,
  setColumnSorts,
  columnSorts,
}) {
  const { t } = useContext(LanguageContext);
  const { selectedTheme } = useContext(ThemeContext);

  const cardBrandTypes = {
    0: "Unknown",
    1: <AmericanTransaction />,
    2: <DinersTransaction />,
    3: <DiscoverTransaction />,
    4: <EftposTransaction />,
    5: <GirocardTransaction />,
    6: <JcbTransaction />,
    7: <MatercardTransaction />,
    8: <UnionTransaction />,
    9: <VisaTransaction />,
  };

  const setColumnSort = (columnIndex) => {
    const columnSortData = { ...columnSorts };
    if (!columnSortData[columnIndex]) columnSortData[columnIndex] = "asc";
    else if (columnSortData[columnIndex] === "asc")
      columnSortData[columnIndex] = "desc";
    else if (columnSortData[columnIndex] === "desc")
      columnSortData[columnIndex] = null;
    setColumnSorts(columnSortData);
  };

  const transactionTypes = Object.fromEntries(
    Object.entries({
      1: "Charge",
      2: "Non-Withdrawable Credits Deposit",
      3: "Non-Withdrawable Credits Purchase Bonus",
      4: "Credits Purchase Bonus",
      5: "Deposit",
      6: "Contract Payment",
      7: "Income Conversion to Credits",
      8: "Order Payment (Non-Withdrawable Credits)",
      9: "Order Payment (Credits)",
      10: "Contract Warranty Hold",
      11: "Contract Warranty Hold Released",
      12: "Withdrawal",
      13: "Contract Warranty Hold Canceled",
      14: "Contract Payment Refund (Non-Withdrawable Credits)",
      15: "Contract Payment Refund (Credits)",
      16: "Contract Payment Refund (Credit Card)",
      17: "Contract Payment Refund (PayPal)",
      100: "Withdrawal",
      200: "Order Payment",
      201: "Order Payment Refund",
      202: "Contract Payment Refund",
      203: "Credits Purchase",
      204: "Credits Deposit",
      300: "Incoming Contract Payment",
      900: "Payment Processing Fee",
    }).map(([key, value]) => [key, t(value)])
  );

  const transactionStatusTypes = {
    100: { label: t("Pending"), className: "bg-soft-primary text-primary" },
    101: { label: t("Processing"), className: "bg-soft-primary text-primary" },
    102: { label: t("Canceled"), className: "bg-soft-danger text-danger" },
    103: { label: t("Failed"), className: "bg-soft-danger text-danger" },
    200: { label: t("Completed"), className: "bg-soft-success text-success" },
    300: {
      label: t("Warranty Hold"),
      className: "bg-softer-warning text-warning",
    },
  };

  const localizeDate = (date) => {
    const localizedDateText = moment(date)
      .tz("America/Los_Angeles")
      .format("MMM DD, YYYY, h:mm A (z)");

    const mappings = {
      Jan: t("Jan"),
      Feb: t("Feb"),
      Mar: t("Mar"),
      Apr: t("Apr"),
      May: t("May"),
      Jun: t("Jun"),
      Jul: t("Jul"),
      Aug: t("Aug"),
      Sep: t("Sep"),
      Oct: t("Oct"),
      Nov: t("Nov"),
      Dec: t("Dec"),
    };

    return localizedDateText
      .split(" ")
      .map((word) => mappings[word] || word)
      .join(" ");
  };

  return (
    <div className="table-responsive datatable-custom">
      <table
        className="table table-thead-bordered table-nowrap card-table"
        style={{ border: "transparent" }}
      >
        <thead className="thead-light">
          <tr>
            {columnsVisibility.id && (
              <th
                className={`table-column-ps-2 sorting${
                  !columnSorts[0]
                    ? ""
                    : columnSorts[0] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(0)}
              >
                {t("ID")}
              </th>
            )}
            {columnsVisibility.description && <th>{t("DESCRIPTION")}</th>}
            {columnsVisibility.payment_method && (
              <th
                className={`sorting${
                  !columnSorts[1]
                    ? ""
                    : columnSorts[1] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(1)}
              >
                {t("PAYMENT METHOD")}
              </th>
            )}
            {columnsVisibility.status && (
              <th
                className={`sorting${
                  !columnSorts[2]
                    ? ""
                    : columnSorts[2] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(2)}
              >
                {t("STATUS")}
              </th>
            )}

            {columnsVisibility.amount && (
              <th
                className={`sorting${
                  !columnSorts[3]
                    ? ""
                    : columnSorts[3] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(3)}
              >
                {t("AMOUNT")}
              </th>
            )}

            {columnsVisibility.date && (
              <th
                className={`sorting${
                  !columnSorts[4]
                    ? ""
                    : columnSorts[4] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(4)}
              >
                {t("DATE")}
              </th>
            )}

            {columnsVisibility.hold_until && (
              <th
                className={`sorting${
                  !columnSorts[5]
                    ? ""
                    : columnSorts[5] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(5)}
              >
                {t("HOLD UNTIL")}
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {!!data?.length ? (
            data?.map((item, index) => (
              <React.Fragment key={index}>
                <tr className="align-middle">
                  {columnsVisibility.id && (
                    <td className="ps-md-3">
                      <div className="responsive-text-applied">
                        {item?.code}
                      </div>
                    </td>
                  )}
                  {columnsVisibility.description && (
                    <td className="ps-md-3">
                      <div
                        className="responsive-text-coupon text-dark"
                        style={{ fontSize: "16px" }}
                      >
                        {transactionTypes[item?.type]}
                      </div>
                    </td>
                  )}
                  {columnsVisibility.payment_method && (
                    <td className="align-middle pt-3">
                      <div
                        className={`d-flex payment-badge align-items-center bg-soft-secondary gap-2 ${
                          item?.method !== 1 ? "ps-3" : "justify-content-center"
                        }`}
                      >
                        <div>
                          {item?.method === 2 &&
                            cardBrandTypes[item?.method_brand]}
                          {item?.method === 3 && <PaypalTransaction />}
                          {item?.method === 4 && <GoogleTransaction />}
                          {item?.method === 5 && <AppleTransaction />}
                          {item?.method === 1 && (
                            <span
                              style={{ fontSize: "12px" }}
                              className={`badge text-secondary py-0 payment-badge-font`}
                            >
                              {t("Internal Transfer")}
                            </span>
                          )}
                        </div>
                        {item?.method !== 1 && (
                          <div className="text-secondary payment-badge-font">
                            &bull;&bull;&bull;&bull; {item?.method_identifier}
                          </div>
                        )}
                      </div>
                    </td>
                  )}

                  {columnsVisibility.status && (
                    <td className="align-middle pt-3">
                      <span
                        className={`payment-status badge ${
                          transactionStatusTypes[item?.status]?.className
                        }`}
                        style={{
                          color: item?.status === 2 ? "#FAC710" : "",
                        }}
                      >
                        {transactionStatusTypes[item?.status]?.label}
                      </span>
                    </td>
                  )}
                  {columnsVisibility.amount && (
                    <td>
                      <div
                        className={`transaction-amount ${
                          item?.status === 1
                            ? "text-secondary-blue"
                            : item?.status === 2
                            ? item?.amount < 0
                              ? "text-orange"
                              : "text-secondary-green"
                            : item?.status === 4
                            ? "text-secondary-red"
                            : "text-declined"
                        }`}
                      >
                        {" "}
                        {`${item?.amount < 0 ? "- $" : "$"}${Math.abs(
                          item?.amount
                        ).toLocaleString()}`}
                      </div>
                    </td>
                  )}
                  {columnsVisibility.date && (
                    <td className="text-dark">
                      <div className="responsive-text-applied">
                        {" "}
                        {localizeDate(item?.created)}
                      </div>
                    </td>
                  )}
                  {columnsVisibility.hold_until && (
                    <td className="text-dark">
                      <div className="responsive-text-applied">
                        {" "}
                        {item?.hold_until_date
                          ? localizeDate(item.hold_until_date)
                          : ""}
                      </div>
                    </td>
                  )}
                </tr>
                {index !== data?.length - 1 && (
                  <tr>
                    <td
                      colSpan="100%"
                      className="px-3 py-0"
                      style={{ border: "transparent" }}
                    >
                      <div
                        style={{
                          width: "calc(100% - 2px)",
                          height: "2px",
                          backgroundColor:
                            selectedTheme === "dark"
                              ? "rgba(45, 50, 54, 1)"
                              : "rgba(45, 50, 54, 0.2)",
                          margin: "0 auto",
                        }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan="100%">
                <div
                  className="text-center p-4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    textAlign: "center",
                  }}
                >
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="dark"
                  />
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="default"
                  />
                  <p className="mb-0">
                    {!data ? t("Loading data") : t("No data to show")}
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TransactionsTable;
